import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Save, Loader2, AlertTriangle,
  History, RotateCcw, X, Check, Eye, Pencil
} from 'lucide-react';
import { contentService } from '../../services/ContentService';

const TEMPLATED_EMBED_ID = process.env.REACT_APP_TEMPLATED_EMBED_ID;

const TemplatedContentEditor = () => {
  const { batchId, contentId } = useParams();

  // Refs
  const isMountedRef = useRef(true);
  const pendingTimersRef = useRef([]);

  // Estados
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Dados
  const [content, setContent] = useState(null);
  const [templateId, setTemplateId] = useState(null);

  // Layers
  const [layers, setLayers] = useState({});
  const [originalLayers, setOriginalLayers] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  // Historico
  const [showHistory, setShowHistory] = useState(false);
  const [editHistory, setEditHistory] = useState([]);

  // ============================================
  // CONTROLE DE MONTAGEM
  // ============================================

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      pendingTimersRef.current.forEach(timer => clearTimeout(timer));
      pendingTimersRef.current = [];
    };
  }, []);

  // ============================================
  // HELPER: setTimeout seguro
  // ============================================

  const safeTimeout = useCallback((fn, delay) => {
    const timer = setTimeout(() => {
      pendingTimersRef.current = pendingTimersRef.current.filter(t => t !== timer);
      if (isMountedRef.current) {
        fn();
      }
    }, delay);
    pendingTimersRef.current.push(timer);
    return timer;
  }, []);

  // ============================================
  // CARREGAR DADOS
  // ============================================

  useEffect(() => {
    loadContentData();
  }, [batchId, contentId]);

  const loadContentData = async () => {
    if (!isMountedRef.current) return;

    setIsLoading(true);
    setError(null);

    try {
      const batchResponse = await contentService.getBatch(batchId);
      if (!isMountedRef.current) return;

      const batchData = batchResponse.data;
      const contentData = batchData.contents?.find(c => c.id === contentId);

      if (!contentData) throw new Error('Conteúdo não encontrado');
      if (contentData.model !== 'templated') throw new Error('Edição disponível apenas para conteúdos Templated');
      if (contentData.status !== 'completed') throw new Error('Conteúdo ainda não foi processado');

      const tplId = contentData.metadata?.template_id;
      if (!tplId) throw new Error('Template ID não encontrado');

      setContent(contentData);
      setTemplateId(tplId);

      const currentLayers = contentData.metadata?.layers || {};
      setLayers(currentLayers);
      setOriginalLayers(JSON.parse(JSON.stringify(currentLayers)));

      setEditHistory(contentData.edit_history || []);
    } catch (err) {
      if (isMountedRef.current) {
        setError(err.message || 'Erro ao carregar');
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  // ============================================
  // IFRAME EMBED URL
  // ============================================

  const getEmbedUrl = useCallback(() => {
    if (!templateId || !TEMPLATED_EMBED_ID) return null;
    const base = `https://app.templated.io/editor/${templateId}?embed=${TEMPLATED_EMBED_ID}`;
    if (Object.keys(layers).length > 0) {
      const encoded = btoa(JSON.stringify(layers));
      return `${base}&layers=${encoded}`;
    }
    return base;
  }, [templateId, layers]);

  // ============================================
  // HANDLERS
  // ============================================

  const handleSave = async () => {
    if (!hasChanges) return;

    setIsSaving(true);
    setSaveSuccess(false);

    try {
      await contentService.saveTemplatedEdit(batchId, contentId, {
        template_id: templateId,
        layers: layers,
        regenerate: true
      });

      if (!isMountedRef.current) return;

      setOriginalLayers(JSON.parse(JSON.stringify(layers)));
      setHasChanges(false);
      setSaveSuccess(true);
      await loadContentData();
      safeTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      if (isMountedRef.current) {
        alert('Erro ao salvar: ' + (err.message || 'Tente novamente'));
      }
    } finally {
      if (isMountedRef.current) setIsSaving(false);
    }
  };

  const handleRevert = async (historyIndex) => {
    if (!window.confirm('Reverter para esta versão?')) return;

    setIsSaving(true);
    try {
      await contentService.restoreContentVersion(batchId, contentId, historyIndex);
      if (!isMountedRef.current) return;
      await loadContentData();
      setShowHistory(false);
    } catch (err) {
      if (isMountedRef.current) alert('Erro ao reverter');
    } finally {
      if (isMountedRef.current) setIsSaving(false);
    }
  };

  // ============================================
  // RENDER - LOADING
  // ============================================

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 text-amber-500 animate-spin mx-auto mb-3" />
          <p className="text-gray-600">Carregando editor...</p>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER - ERROR
  // ============================================

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Erro</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-5 py-2.5 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600 transition-all"
            >
              Recarregar
            </button>
            <Link
              to={`/contents/batch/${batchId}`}
              className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all"
            >
              Voltar
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const embedUrl = getEmbedUrl();

  // ============================================
  // RENDER - EDITOR
  // ============================================

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to={`/contents/batch/${batchId}`}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </Link>
              <div>
                <h1 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Pencil className="w-4 h-4 text-amber-500" />
                  Editar Post
                </h1>
                <p className="text-sm text-gray-500">
                  {content?.metadata?.layout_agent_name || content?.metadata?.layout_type || 'Templated'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {editHistory.length > 0 && (
                <button
                  onClick={() => setShowHistory(true)}
                  className="p-2.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Histórico"
                >
                  <History className="w-5 h-5" />
                </button>
              )}

              <button
                onClick={handleSave}
                disabled={!hasChanges || isSaving}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all ${
                  hasChanges && !isSaving
                    ? 'bg-amber-500 text-white hover:bg-amber-600 shadow-sm'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : saveSuccess ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {isSaving ? 'Salvando...' : saveSuccess ? 'Salvo!' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Conteudo - iframe full width */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-amber-500" />
              <span className="font-medium text-gray-900">Editor Templated</span>
            </div>
            {embedUrl && (
              <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                ● Conectado
              </span>
            )}
          </div>

          {embedUrl ? (
            <iframe
              src={embedUrl}
              title="Templated Editor"
              className="w-full border-0"
              style={{ minHeight: '80vh' }}
              allow="clipboard-write; clipboard-read"
            />
          ) : (
            <div
              className="w-full flex items-center justify-center"
              style={{ minHeight: '400px' }}
            >
              <div className="text-center">
                <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <p className="text-sm text-gray-500">
                  {!TEMPLATED_EMBED_ID
                    ? 'REACT_APP_TEMPLATED_EMBED_ID não configurado'
                    : 'Template ID não encontrado'}
                </p>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Modal Historico */}
      {showHistory && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowHistory(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <History className="w-5 h-5 text-amber-500" />
                Histórico
              </h2>
              <button
                onClick={() => setShowHistory(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-all"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {editHistory.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Nenhum histórico</p>
              ) : (
                <div className="space-y-3">
                  {editHistory.map((entry, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl"
                    >
                      {entry.previous_url && (
                        <img
                          src={entry.previous_url}
                          alt={`Versão ${index + 1}`}
                          className="w-14 h-14 object-cover rounded-lg"
                        />
                      )}

                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm">
                          Versão {editHistory.length - index}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(entry.edited_at).toLocaleString('pt-BR')}
                        </p>
                      </div>

                      <button
                        onClick={() => handleRevert(index)}
                        disabled={isSaving}
                        className="px-3 py-2 text-sm font-medium text-amber-600 hover:bg-amber-50 rounded-lg transition-all flex items-center gap-1.5"
                      >
                        <RotateCcw className="w-4 h-4" />
                        Restaurar
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplatedContentEditor;
